import { useState } from 'react'
import { X, ArrowLeft } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import TemplateGallery from './TemplateGallery'
import { useUser } from '../hooks/useUser'
import type { App } from '../lib/api'

interface TemplateGalleryModalProps {
  onClose: () => void
  initialTemplateName?: string  // Open directly to this template's detail
}

type ModalView = { mode: 'grid' } | { mode: 'detail'; name: string }

export default function TemplateGalleryModal({ onClose, initialTemplateName }: TemplateGalleryModalProps) {
  const navigate = useNavigate()
  const user = useUser()
  const showDisabledBadge = user.isAdmin && !user.features.templatesVisibleToUsers
  const [searchQuery, setSearchQuery] = useState('')
  const [currentView, setCurrentView] = useState<ModalView>(
    initialTemplateName ? { mode: 'detail', name: initialTemplateName } : { mode: 'grid' }
  )
  const [backTrigger, setBackTrigger] = useState(0)

  function handleTemplateCreated(app: App) {
    onClose()
    navigate(`/apps/${app.name}`)
  }

  const isDetail = currentView.mode === 'detail'
  const detailName = isDetail ? currentView.name : ''

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-xl w-full max-w-3xl h-[80vh] flex flex-col overflow-hidden">
        {/* Header — fixed at top with padding */}
        <div className="px-12 pt-6 pb-3 shrink-0">
          <div className="flex items-center justify-between">
            {isDetail ? (
              <div className="flex items-center gap-2 min-w-0">
                <button
                  onClick={() => {
                    setCurrentView({ mode: 'grid' })
                    setBackTrigger(prev => prev + 1)
                  }}
                  className="p-1.5 rounded-lg hover:bg-secondary transition-colors text-muted-foreground hover:text-foreground"
                  aria-label="Back to templates"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <h2 className="text-xl font-semibold truncate">{detailName}</h2>
                {showDisabledBadge && <DisabledBadge />}
              </div>
            ) : (
              <div className="flex items-center gap-2 min-w-0">
                <h2 className="text-xl font-semibold">Choose a Template</h2>
                {showDisabledBadge && <DisabledBadge />}
              </div>
            )}
            <button
              onClick={onClose}
              className="p-1 rounded hover:bg-secondary transition-colors shrink-0"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Search — only on grid view */}
          {!isDetail && (
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search templates..."
              className="w-full px-3 py-2 mt-3 text-sm rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary placeholder:text-muted-foreground"
            />
          )}
        </div>

        {/* Scrollable content — scrollbar at modal edge, content padded inside */}
        <div className="flex-1 overflow-y-auto min-h-0">
          <div className="px-12 pb-6">
            <TemplateGallery
              onSelectTemplate={handleTemplateCreated}
              onClose={onClose}
              searchQuery={searchQuery}
              onViewChange={(view) => setCurrentView(view)}
              externalBackToGrid={backTrigger}
              initialTemplateName={initialTemplateName}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

function DisabledBadge() {
  return (
    <span
      className="px-2 py-0.5 rounded-md text-xs font-medium bg-primary/10 text-primary border border-primary/30 cursor-help shrink-0"
      title="This feature is in testing — only admins and allowlisted testers can see it. Regular users won't see this option until you enable it in Templates settings."
    >
      Disabled
    </span>
  )
}
